import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { Loader2, LayoutDashboard } from "lucide-react";
import { useAdminApi, errMsg, money } from "../lib/api";
import { useLang, useTitle } from "../lib/i18n";
import { PageHead } from "../components/site/SiteLayout";
import { fmtTime } from "../components/booking/SlotPicker";

const STAFF = ["admin", "manager", "cleaner"];

function AccountBody() {
  const { t, lang } = useLang();
  const call = useAdminApi();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { call("get", "/account").then(setD).catch((e) => setErr(errMsg(e))); }, [call]);
  if (err) return <p className="p-10 text-red-600">{err}</p>;
  if (!d) return <div className="p-20 grid place-items-center"><Loader2 className="animate-spin text-sky-600" /></div>;
  return (
    <div>
      <PageHead title={t.account.title} sub={`${t.account.hello}, ${d.name || d.email}. ${t.account.note}`} />
      <section className="max-w-5xl mx-auto px-4 py-12 space-y-8" data-testid="account-page">
        {STAFF.includes(d.role) && (
          <Link to="/admin" className="btn-dark" data-testid="account-dashboard-link"><LayoutDashboard size={16} />{t.account.dashboard}</Link>
        )}
        <div className="card">
          <div className="flex items-center justify-between mb-4"><h2 className="text-xl font-semibold">{t.account.bookings}</h2><Link to="/book" className="btn-primary !py-2 text-sm" data-testid="account-book-link">{t.account.book}</Link></div>
          {d.bookings.length === 0 ? <p className="text-slate-500" data-testid="account-no-bookings">{t.account.none}</p> : d.bookings.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0 text-sm" data-testid={`account-booking-${b.number}`}>
              <div><p className="font-semibold">{lang === "es" ? b.price.service_name_es : b.price.service_name_en}</p><p className="text-slate-500">{b.number} · {b.date} {fmtTime(b.time)}</p></div>
              <div className="flex items-center gap-4">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-50 text-sky-800">{t.status[b.status] || b.status}</span>
                <span>{money(b.price.total)}{b.balance_due > 0 && !["cancelled", "expired"].includes(b.status) && <span className="text-amber-700"> · {t.view.balance} {money(b.balance_due)}</span>}</span>
                <Link to={`/booking/${b.id}?token=${b.token}`} className="font-semibold text-sky-700" data-testid={`account-open-booking-${b.number}`}>{t.account.open}</Link>
              </div>
            </div>
          ))}
        </div>
        {d.quotes.length > 0 && (
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">{t.account.quotes}</h2>
            {d.quotes.map((q) => (
              <div key={q.id} className="flex justify-between py-2 text-sm border-b border-slate-100 last:border-0">
                <span>{q.service_name} · {q.created_at.slice(0, 10)} · {t.quoteStatus[q.status] || q.status}</span>
                {q.quote && <Link to={`/quote/${q.quote.id}?token=${q.quote.token}`} className="font-semibold text-sky-700">{t.account.viewQuote} ({money(q.quote.amount)})</Link>}
              </div>
            ))}
          </div>
        )}
        {d.payments.length > 0 && (
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">{t.account.payments}</h2>
            {d.payments.map((p, i) => (
              <div key={i} className="flex justify-between py-2 text-sm border-b border-slate-100 last:border-0">
                <span>{p.paid_at?.slice(0, 10)} · {p.kind}{p.refunded_amount > 0 ? ` (refunded ${money(p.refunded_amount)})` : ""}</span>
                <span className="flex gap-3 font-semibold">{money(p.amount)}{p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-sky-700">{t.view.receipt}</a>}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default function Account() {
  const { t } = useLang();
  useTitle(t.account.title);
  return (
    <>
      <Show when="signed-in"><AccountBody /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
